import {
  useDeviceSize,
  Breakpoint,
} from '@grubhubprod/umami-library/dist/hooks/useDeviceSize';
import {
  Square,
  Image,
  Flex,
  Section,
} from '@grubhubprod/gh-cookbook-react/dist';
import classnames from 'classnames';
import React, { useState, useEffect, useRef } from 'react';
import { Transition } from 'react-transition-group';
import { Helmet } from 'react-helmet-async';
import SignupNavBar from 'src/components/Navigation/SignupNavBar';
import { getWindow } from 'src/utilities/window';
import { getConfig } from 'src/utilities/config';
import {
  ENTERPRISE_STEP_CONTENT,
  useEnterpriseSignupStatus,
} from 'src/components/EnterpriseSignupStatusProvider/EnterpriseSignupStatusProvider';
import { SignupProgressTracker } from 'src/components/common/features/Signup/components/SignupProgressTracker/SignupProgressTracker';
import { FIXED_SIGNUP_FOOTER_ID } from 'src/components/common/features/Signup/constants';

const wizardStepEnterDuration = 500;
const wizardStepExitDuration = 1000;
const defaultWizardStepStyles = {
  transition: `all ${wizardStepEnterDuration}ms ease-in`,
  opacity: 0,
};
const wizardStepTransitionStyles = {
  entering: {
    opacity: 1,
  },
  entered: {
    opacity: 1,
  },
  exiting: {
    opacity: 0,
  },
  exited: {
    opacity: 0,
  },
};

const FIXED_FOOTER_HEIGHT = '76px';

export function EnterpriseSignupWizard(): JSX.Element {
  const { breakpoint } = useDeviceSize(getWindow());
  const { activeStep, currentStepNumber, getSteps } =
    useEnterpriseSignupStatus();
  const footerRef = useRef<HTMLDivElement>(null);
  const [isMounted, setIsMounted] = useState(false);
  const [currentActiveStep, setCurrentActiveStep] = useState(activeStep);
  const [wizardSignupSteps] = useState(getSteps());
  const {
    site: { enterpriseTitle: siteTitle },
  } = getConfig();

  const isTablet = breakpoint >= Breakpoint.sm;
  const isPhone = !isTablet;

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (currentActiveStep !== activeStep) {
      // give the exit animation time to finish before updating
      setTimeout(() => {
        setCurrentActiveStep(activeStep);
      }, wizardStepEnterDuration);
    }
  }, [activeStep, currentActiveStep]);

  return (
    <>
      <Helmet defer={false}>
        <title>
          {`${siteTitle} | Enterprise Setup - Step ${currentStepNumber}`}
        </title>
      </Helmet>
      <SignupNavBar isEnterprise />
      <section
        className={classnames('signupWizard', {
          twoColumns: isTablet,
        })}
      >
        <Flex
          flexDirection="column"
          backgroundColor={isPhone ? 'globals.white' : 'globals.stone10'}
          className="signupWizard_signupLeftSidebar"
        >
          <Flex
            flexGrow={isPhone ? 0 : 1}
            flexDirection="column"
            justifyContent="space-between"
            className={classnames('', {
              signupWizard_hasFixedHeader: isPhone,
            })}
          >
            <Square
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...(!isTablet && { inset: 'md' })}
              overflowY={isPhone ? 'auto' : 'initial'}
              height="100%"
              margin={isTablet ? '0 48px' : '0'}
              paddingTop={isPhone ? '0' : '96px'}
            >
              <SignupProgressTracker isEnterprise steps={wizardSignupSteps} />
            </Square>
            {isPhone && (
              <Square
                flexBasis="auto"
                flexGrow={0}
                flexShrink={0}
                maxHeight={FIXED_FOOTER_HEIGHT}
                id={FIXED_SIGNUP_FOOTER_ID}
              />
            )}
            {isTablet && (
              <Square
                width="100%"
                inset="lg"
                className="signupWizard_illustrationContainer"
              >
                <Image
                  src="https://media-cdn.grubhub.com/image/upload/v1601990412/corporate/Hero_Illustration.svg"
                  alt=""
                  role="presentation"
                />
              </Square>
            )}
          </Flex>
        </Flex>
        <Flex
          flexDirection="column"
          height="100vh"
          className="signupWizard_signupMainContentContainer"
        >
          <Flex
            alignItems="flex-start"
            justifyContent="center"
            paddingTop="48px"
            flexGrow={1}
            className={classnames('signupWizard_signupMainContent', {
              mounted: isMounted,
            })}
          >
            {isTablet && footerRef.current && (
              <Transition
                in={currentActiveStep === activeStep}
                timeout={{
                  enter: wizardStepEnterDuration,
                  exit: wizardStepExitDuration,
                }}
                mountOnEnter
                unmountOnExit
              >
                {(transitionState) => (
                  <Section
                    inset="md"
                    width="100%"
                    style={{
                      ...defaultWizardStepStyles,
                      ...wizardStepTransitionStyles[transitionState],
                    }}
                  >
                    {ENTERPRISE_STEP_CONTENT[currentActiveStep]}
                  </Section>
                )}
              </Transition>
            )}
          </Flex>
          {isTablet && (
            <Square
              ref={footerRef}
              flexBasis="auto"
              flexGrow={0}
              flexShrink={0}
              data-testid={FIXED_SIGNUP_FOOTER_ID}
              id={FIXED_SIGNUP_FOOTER_ID}
            />
          )}
        </Flex>
      </section>
    </>
  );
}
